/**
 * Hand-coded daily life and routines situational phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 */

import type { SituationCard } from '../../situations/types.js';

const dailyLife: SituationCard[] = [
  {
    id: 'daily.good-morning',
    situationId: 'daily.good-morning',
    front: 'Good morning! How did you sleep?',
    back: 'おはようございます。よく眠れましたか？',
    category: 'daily-life',
    cefr: 'A1',
    translations: { ja: 'おはようございます。よくねむれましたか？' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.what-doing',
    situationId: 'daily.what-doing',
    front: 'What are you up to today?',
    back: '今日は何の予定ですか？',
    category: 'daily-life',
    cefr: 'A1',
    translations: { ja: 'きょうはなんのよていですか？' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.running-errand',
    situationId: 'daily.running-errand',
    front: "I'm just running a few errands. Need anything?",
    back: 'ちょっと用事を済ませてくるね。何か買ってきましょうか？',
    category: 'daily-life',
    cefr: 'A2',
    translations: { ja: 'ちょっとようじをすませてくるね。なにかかってきましょうか？' },
    speechAct: 'offering',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.tired',
    situationId: 'daily.tired',
    front: "I'm pretty tired — I think I'll turn in early tonight.",
    back: 'けっこう疲れてるから、今夜は早めに休もうかな。',
    category: 'daily-life',
    cefr: 'A2',
    translations: { ja: 'けっこうつかれてるから、こんやははやめにやすもうかな。' },
    speechAct: 'opinion',
    formality: 'casual',
    channel: 'in-person',
    notes: {
      en: '"Turn in" = "go to bed." A casual idiom worth memorizing.',
      ja: '"turn in"は「寝る」のくだけた表現。'
    }
  },
  {
    id: 'daily.heading-out',
    situationId: 'daily.heading-out',
    front: "I'm heading out — I'll see you later!",
    back: '行ってきます！また後でね。',
    category: 'daily-life',
    cefr: 'A1',
    translations: { ja: 'いってきます！またあとでね。' },
    speechAct: 'closing',
    formality: 'casual',
    channel: 'in-person',
    pragmaticNote: {
      en: 'English doesn\'t have a direct equivalent of 行ってきます / いってらっしゃい — "I\'m heading out" + "See you later" / "Have a good one" is the closest fit.',
      ja: '日本語の「行ってきます／いってらっしゃい」にぴったり対応する英語はなく、"I\'m heading out" や "See you later" が近い表現。'
    }
  },
  {
    id: 'daily.busy-week',
    situationId: 'daily.busy-week',
    front: "It's been a really busy week — I haven't had a moment to myself.",
    back: '今週は本当に忙しくて、自分の時間がまったく取れていません。',
    category: 'daily-life',
    cefr: 'B1',
    translations: { ja: 'こんしゅうはほんとうにいそがしくて、じぶんのじかんがまったくとれていません。' },
    speechAct: 'opinion',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.weekend-plans',
    situationId: 'daily.weekend-plans',
    front: 'Any plans for the weekend?',
    back: '週末は何か予定がありますか？',
    category: 'daily-life',
    cefr: 'A2',
    translations: { ja: 'しゅうまつはなにかよていがありますか？' },
    speechAct: 'opening',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.help-around',
    situationId: 'daily.help-around',
    front: 'Need a hand with anything?',
    back: '何か手伝いましょうか？',
    category: 'daily-life',
    cefr: 'A2',
    translations: { ja: 'なにかてつだいましょうか？' },
    speechAct: 'offering',
    formality: 'casual',
    channel: 'in-person'
  },
  {
    id: 'daily.catch-up-soon',
    situationId: 'daily.catch-up-soon',
    front: "Let's catch up soon — it's been ages.",
    back: 'そのうちゆっくり話そうよ。久しぶりすぎるから。',
    category: 'daily-life',
    cefr: 'B1',
    translations: { ja: 'そのうちゆっくりはなそうよ。ひさしぶりすぎるから。' },
    speechAct: 'closing',
    formality: 'casual',
    channel: 'in-person',
    notes: {
      en: '"It\'s been ages" = "it\'s been a long time." Common conversational hyperbole.',
      ja: '"It\'s been ages"は「すごく久しぶり」の感覚的な表現。'
    }
  }
];

export default dailyLife;
